import { type AxiosResponse } from 'axios'

import client from '@/services/client'
import type {
  ChallengeOptions,
  IAuth,
  IAuthResponse,
  IConfirmEmailChangeResponse,
  IExchangeCodeResponse,
  IGenerateAuthCodeResponse,
  IResetPasswordPayload,
  ISetPasswordTokenPayload,
  ITwoFactorMethod,
  ITwoFactorPendingMethod,
  IVerifyEmailResponse,
  TwoFactorMethods,
} from '@/types/AuthTypes'

// #region Auths
// region Auths

const login = async (
  email: string,
  password: string
): Promise<AxiosResponse<IAuthResponse>> =>
  await client.post<IAuthResponse>(`/auths/login`, { email, password })

const sendResetPasswordLink = async (
  email: string
): Promise<AxiosResponse<void>> =>
  await client.post(`/auths/password/link`, { email })

const refreshAuth = async (
  refreshToken: string
): Promise<AxiosResponse<IAuth>> =>
  client.post<IAuth>('/auths/refresh', { refreshToken })

const setNewPassword = async (
  payload: IResetPasswordPayload
): Promise<AxiosResponse<void>> =>
  await client.post(`/auths/password/reset`, payload)

const exchangeCodeForPasswordToken = async (
  exchangeCode: string
): Promise<AxiosResponse<IExchangeCodeResponse>> => {
  return await client.post(`/auths/password/exchange-code`, { exchangeCode })
}

const setNewPasswordToken = async (
  payload: ISetPasswordTokenPayload
): Promise<AxiosResponse<void>> =>
  await client.post(`/auths/password/set`, payload)

const resendUserValidationMail = async (
  email: string
): Promise<AxiosResponse<void>> => {
  return client.post(`/auths/email/resend`, { email })
}

const logout = async (): Promise<AxiosResponse<void>> =>
  await client.post(`/auths/logout`)

// endregion
// #endregion

// #region Auths 2FA
// region

const confirmLogin = async (
  code: string,
  methodUuid: string
): Promise<AxiosResponse<IAuthResponse>> =>
  await client.post<IAuthResponse>(`/auths/login/2fa`, { code, methodUuid })

const challenge = async (
  purpose: ChallengeOptions,
  methodUuid: string
): Promise<AxiosResponse<string>> =>
  await client.post<string>(`/auths/2fa/challenge`, {
    purpose,
    methodUuid,
  })

const challengeLogin = async (
  methodUuid: string
): Promise<AxiosResponse<string>> => await challenge(`login`, methodUuid)

const getTwoFactorMethodTypes = async (): Promise<AxiosResponse<string[]>> =>
  await client.get(`/auths/2fa/method-types`)

const getUserTwoFactorMethods = async (): Promise<
  AxiosResponse<ITwoFactorMethod[]>
> => await client.get(`/auths/2fa/methods`)

const addFirstTwoFactorMethod = async (
  newMethod: TwoFactorMethods,
  password: string
): Promise<AxiosResponse<ITwoFactorPendingMethod>> =>
  await client.post(`/auths/2fa/methods`, {
    newMethod,
    password,
    methodUuid: null,
    code: null,
  })

const addAnotherTwoFactorMethod = async (
  newMethod: TwoFactorMethods,
  methodUuid: string,
  code: string
): Promise<AxiosResponse<ITwoFactorPendingMethod>> =>
  await client.post(`/auths/2fa/methods`, {
    newMethod,
    password: null,
    methodUuid,
    code,
  })

const setDefaultTwoFactorMethod = async (
  methodUuid: string
): Promise<AxiosResponse<ITwoFactorMethod>> =>
  await client.patch(`/auths/2fa/methods/default`, { methodUuid })

const confirmTwoFactorMethod = async (
  methodUuid: string,
  code: string
): Promise<AxiosResponse<ITwoFactorMethod>> => {
  return await client.post(`/auths/2fa/methods/${methodUuid}/confirm`, { code })
}

const deleteTwoFactorMethod = async (
  methodUuid: string,
  confirmMethodUuid: string,
  code: string,
  password: string
): Promise<AxiosResponse<void>> =>
  await client.delete(`/auths/2fa/methods/${methodUuid}`, {
    params: {
      methodUuid,
      confirmMethodUuid,
      code,
      password,
    },
  })

const authGenerateQA = async (
  methodUuid: string
): Promise<AxiosResponse<IGenerateAuthCodeResponse>> =>
  await client.post(`/auths/2fa/totp/setup`, { methodUuid })

//endregion
// #endregion

const getVerifyEmailTokenFromUrl = async (
  link: string
): Promise<AxiosResponse<IVerifyEmailResponse>> => {
  return await client.get(link, { baseURL: '' })
}

const confirmEmailChange = async (
  link: string
): Promise<AxiosResponse<IConfirmEmailChangeResponse>> =>
  await client.get<IConfirmEmailChangeResponse>(link)

export const AuthRepository = {
  login,
  sendResetPasswordLink,
  setNewPassword,
  exchangeCodeForPasswordToken,
  setNewPasswordToken,
  resendUserValidationMail,
  logout,
  confirmLogin,
  challenge,
  challengeLogin,
  getTwoFactorMethodTypes,
  getUserTwoFactorMethods,
  addFirstTwoFactorMethod,
  addAnotherTwoFactorMethod,
  setDefaultTwoFactorMethod,
  confirmTwoFactorMethod,
  deleteTwoFactorMethod,
  authGenerateQA,
  getVerifyEmailTokenFromUrl,
  confirmEmailChange,
  refreshAuth,
}
